import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,

  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Download as DownloadIcon,

  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { auditService } from '../services/auditService';
import {
  DocumentAuditLog,
  DocumentAuditHistoryRequest,
  DocumentAuditExportRequest,
  AUDIT_ACTION_LABELS,
  AUDIT_STATUS_COLORS
} from '../types/audit';

const AuditExportPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [auditLogs, setAuditLogs] = useState<DocumentAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // Filters
  const [filters, setFilters] = useState<DocumentAuditHistoryRequest>({
    document_id: '',
    document_type: '',
    operation: '',
    admin_id: '',
    start_date: '',
    end_date: '',
    limit,
    offset: 0
  });

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv'>('pdf');

  useEffect(() => {
    loadAuditHistory();
  }, [page]);

  const loadAuditHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        ...filters,
        limit,
        offset: (page - 1) * limit
      };

      const response = await auditService.getAuditHistory(params);
      
      setAuditLogs(response.audit_logs);
      setTotalRecords(response.total);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (err: any) {
      console.error('Failed to load audit history:', err);
      setError('Failed to load audit history');
      enqueueSnackbar('Failed to load audit history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadAuditHistory();
  };

  const handleClearFilters = () => {
    setFilters({
      document_id: '',
      document_type: '',
      operation: '',
      admin_id: '',
      start_date: '',
      end_date: '',
      limit,
      offset: 0
    });
    setPage(1);
  };

  const handleFilterChange = (field: keyof DocumentAuditHistoryRequest, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const exportRequest: DocumentAuditExportRequest = {
        ...filters,
        export_format: exportFormat
      };

      const response = await auditService.exportAuditHistory(exportRequest);

      if (response.success && response.filename) {
        // Download the file
        const blob = await auditService.downloadAuditExport(response.filename);
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        enqueueSnackbar(`Audit export downloaded successfully (${response.total_records} records)`, { 
          variant: 'success' 
        });
      } else {
        throw new Error(response.errors.join(', ') || 'Export failed');
      }

      setExportDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to export audit history:', err);
      enqueueSnackbar('Failed to export audit history', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'dd/MM/yyyy HH:mm:ss');
  };

  const getActionLabel = (action: string) => {
    return AUDIT_ACTION_LABELS[action] || action.charAt(0).toUpperCase() + action.slice(1);
  };

  const getStatusColor = (status: string) => {
    return AUDIT_STATUS_COLORS[status] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Typography color="text.primary">
          Audit Export
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Document Audit Export
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search, filter, and export document audit history
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAuditHistory}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={loading || auditLogs.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search & Filter
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Document ID"
                value={filters.document_id}
                onChange={(e) => handleFilterChange('document_id', e.target.value)}
                placeholder="Enter document ID"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={filters.document_type}
                  onChange={(e) => handleFilterChange('document_type', e.target.value)}
                  label="Document Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="estimate">Estimate</MenuItem>
                  <MenuItem value="sales_order">Sales Order</MenuItem>
                  <MenuItem value="invoice">Invoice</MenuItem>
                  <MenuItem value="sales_return">Sales Return</MenuItem>
                  <MenuItem value="delivery_challan">Delivery Challan</MenuItem>
                  <MenuItem value="purchase_order">Purchase Order</MenuItem>
                  <MenuItem value="purchase_bill">Purchase Bill</MenuItem>
                  <MenuItem value="purchase_return">Purchase Return</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Operation</InputLabel>
                <Select
                  value={filters.operation}
                  onChange={(e) => handleFilterChange('operation', e.target.value)}
                  label="Operation"
                >
                  <MenuItem value="">All Operations</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="convert">Convert</MenuItem>
                  <MenuItem value="send">Send</MenuItem>
                  <MenuItem value="approve">Approve</MenuItem>
                  <MenuItem value="reject">Reject</MenuItem>
                  <MenuItem value="cancel">Cancel</MenuItem>
                  <MenuItem value="confirm">Confirm</MenuItem>
                  <MenuItem value="dispatch">Dispatch</MenuItem>
                  <MenuItem value="deliver">Deliver</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Admin ID"
                value={filters.admin_id}
                onChange={(e) => handleFilterChange('admin_id', e.target.value)}
                placeholder="Enter admin ID"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Audit Records ({totalRecords} total)
            </Typography>
            {loading && <CircularProgress size={24} />}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {auditLogs.length === 0 && !loading ? (
            <Alert severity="info">
              No audit records found. Try adjusting your search filters.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Document Type</TableCell>
                      <TableCell>Document ID</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.resource_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {log.resource_id?.substring(0, 8) || 'N/A'}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {getActionLabel(log.action)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.event_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status.toUpperCase()}
                            color={getStatusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.actor_type === 'admin' ? 'Admin' : log.actor_type}
                          </Typography>
                          {log.actor_id && (
                            <Typography variant="caption" color="text.secondary">
                              {log.actor_id.substring(0, 8)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {log.ip_address || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Showing {auditLogs.length} of {totalRecords} records
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Audit History</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Export the current search results ({totalRecords} records) to a file.
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Export Format
              </Typography>
              <RadioGroup
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'csv')}
              >
                <FormControlLabel
                  value="pdf"
                  control={<Radio />}
                  label="PDF - Formatted report with detailed information"
                />
                <FormControlLabel
                  value="csv"
                  control={<Radio />}
                  label="CSV - Spreadsheet format for data analysis"
                />
              </RadioGroup>
            </FormControl>

            {totalRecords > 1000 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Large export detected ({totalRecords} records). The export may take some time to complete.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditExportPage;